import { Link, useLocation } from 'react-router-dom';
import type { SeasonStanding } from '../../api/seasons';
import { NEUTRAL_TIER_COLOR, TIER_COLORS } from './SeasonStrip';
import { originOf } from '../../lib/back-origin';

/**
 * One standings row: rank / tier dot / name / points.
 *
 * Split out from `StandingsList` deliberately — Task 12 links each row to
 * the player's public profile, and needs to wrap the name in a `<Link>`
 * without touching how the list is built or iterated. Adding that link
 * here later is additive; it does not require restructuring this file or
 * its caller. Reused as-is for the pinned row (see `StandingsList` below),
 * so both render identically — same markup, same testids, same locators.
 */
function StandingsRow({ row, highlighted }: { row: SeasonStanding; highlighted: boolean }) {
  const location = useLocation();
  const tierColor = row.tier ? TIER_COLORS[row.tier] : NEUTRAL_TIER_COLOR;

  return (
    <li
      data-testid={`standings-row-${row.rank}`}
      className={`flex items-center gap-3 border-l-4 px-4 py-2.5 ${
        highlighted ? 'border-emerald-700 bg-emerald-50' : 'border-transparent'
      }`}
    >
      <span className="w-8 text-center text-sm font-bold tabular-nums text-stone-500">
        {row.rank}
      </span>
      <span
        aria-hidden="true"
        className="inline-flex h-2.5 w-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: tierColor }}
      />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-stone-900">
        <Link
          to={`/player/${encodeURIComponent(row.leaderboard_name)}`}
          // Read here rather than threaded in as a prop: the row is rendered
          // from both /season and /season/:key, and must send the viewer back
          // to whichever they were actually looking at.
          state={{ from: originOf(location) }}
          data-testid={`standings-profile-link-${row.rank}`}
          className="hover:underline"
        >
          {row.leaderboard_name}
        </Link>
        {highlighted ? (
          <span data-testid="standings-you-marker" className="ml-1 text-emerald-800">
            (you)
          </span>
        ) : null}
      </span>
      <span className="text-sm font-bold tabular-nums text-stone-900">
        {row.points.toLocaleString('en-GB')}
      </span>
    </li>
  );
}

/**
 * A page of season standings, with the viewer's own row picked out.
 *
 * `meRank` is used ONLY to decide which row already on THIS page gets the
 * inline highlight. Standings rows carry no `is_me` flag of their own, so
 * matching by rank is the only way to identify the viewer's row; it needs
 * nothing but a number, so it works even while auth is still settling.
 *
 * A viewer ranked BELOW this block is not handled here. The page renders a
 * second list of their neighbours instead (see Season.tsx's "your position"
 * block). A single pinned row used to live in this component; the
 * neighbourhood replaced it — same trigger, but it shows the players they
 * can actually overtake, and takes names from the server rather than
 * rebuilding one from the auth context.
 */
export function StandingsList({
  standings,
  meRank = null,
}: {
  standings: SeasonStanding[];
  meRank?: number | null;
}) {
  return (
    <>
      {standings.length === 0 ? (
        <p data-testid="standings-empty" className="mt-4 text-center text-sm text-stone-600">
          No standings yet — be the first to finish a board this season.
        </p>
      ) : (
        <ol
          data-testid="standings-list"
          className="divide-y divide-tile-edge/60 rounded-lg border border-tile-edge bg-[#fffdf6] shadow-[0_3px_0_rgba(217,199,154,0.9)]"
        >
          {standings.map((row) => (
            <StandingsRow
              key={row.rank}
              row={row}
              highlighted={meRank !== null && row.rank === meRank}
            />
          ))}
        </ol>
      )}
    </>
  );
}
